import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { SquareArrowLeft } from 'lucide-react';
import toast from 'react-hot-toast';

import { useGetInvestmentDetails, useUpdateInvestment } from '../../repositories/investment-repository';
import type { InvestmentAmount, InvestmentUpdatePayload } from '../../services/applications-services';
import { isEditableStatus, statusLabel } from '@/utils/application-status';
import { CapitalInvestment, Construction, LicenceDetails, Workforce } from '../fragments/icons';

import VerticalStepper from '../fragments/vertical-stepper';
import Button from '@/components/buttons/Button';
import ProjectForm from '../form/project-details-form';
import CapitalInvestmentForm from '../form/capital-investment-form';
import LandConstractionsForm from '../form/land-constractions-form';
import WorkForcesForm from '../form/work-forces-form';

/**
 * Editing an investment application the applicant already submitted.
 *
 * The four content steps of the create wizard are imported unchanged and
 * prefilled from GET /investment/{applicationId}. The wizard's two remaining
 * steps are left out on purpose: step 1 is the application-fee payment, already
 * taken at submission, and the Submission step exists to open the inspection-fee
 * modal — neither belongs in a correction.
 */

const STEP_FIELDS: Record<number, string[]> = {
    1: [
        'projectName', 'projectPoBox', 'emailAddress', 'zone', 'region', 'district',
        'municipal', 'street', 'postCode', 'poBox', 'projectActivities',
        'numberOfRooms', 'investMentAmount',
    ],
    2: [
        'companyName', 'estimatedInvestment', 'personalContribution', 'loan',
        'institutionName', 'bankName', 'bankAddress',
    ],
    3: [
        'landSizeHectares', 'existingBuildings', 'constructionCompletionTime',
        'projectLifespan',
    ],
    4: [
        'localPermanentEmployees', 'localTemporaryEmployees',
        'foreignPermanentEmployees', 'foreignTemporaryEmployees',
    ],
};

/** "" for anything the record does not carry, so the field renders empty rather
 *  than as the string "undefined". Never a substitute value. */
function text(value: unknown): string {
    if (value === null || value === undefined) return '';
    return String(value);
}

function optionalText(value: unknown): string | undefined {
    if (value === null || value === undefined) return undefined;
    const trimmed = String(value).trim();
    return trimmed === '' ? undefined : trimmed;
}

function optionalNumber(value: unknown): number | undefined {
    if (value === null || value === undefined || value === '') return undefined;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
}

/**
 * CapitalInvestment.fromDto dereferences `.amount` without a null check, so an
 * amount object is only ever sent once there is a number to put in it.
 */
function toAmount(value: unknown, currency: unknown): InvestmentAmount | undefined {
    const amount = optionalNumber((value as any)?.amount ?? value);
    if (amount === undefined) return undefined;
    return { amount, currency: optionalText((value as any)?.currency ?? currency) };
}

function toActivities(value: unknown): string[] | undefined {
    if (Array.isArray(value)) {
        const activities = value.map((entry) => text(entry).trim()).filter(Boolean);
        return activities.length > 0 ? activities : undefined;
    }
    const single = optionalText(value);
    return single ? [single] : undefined;
}

function toFormValues(investment: any) {
    const business = investment?.businessDetails ?? {};
    const capital = investment?.capitalInvestment ?? {};
    const construction = investment?.construction ?? {};
    const workforce = investment?.workforce ?? {};

    return {
        // Project details
        projectName: text(business.projectName),
        projectPoBox: text(business.poBox),
        // The read payload carries no contact details for an investment
        // application — BusinessDetails declares neither, so these stay empty
        // unless the applicant fills them in.
        emailAddress: '',
        phoneNumber: '',
        zone: text(business.zone),
        region: text(business.region),
        district: text(business.district),
        // BusinessDetails has no municipal field; nothing to prefill from.
        municipal: '',
        street: text(business.street),
        postCode: text(business.postCode),
        poBox: text(business.poBox),
        projectActivities: business.projectActivities ?? [],
        numberOfRooms: business.numberOfRooms ?? '',
        investMentAmount: capital.estimatedInvestmentAmount ?? '',
        projectDescription: text(business.describeTargetAudience),

        // Capital investment
        companyName: text(business.businessName),
        estimatedInvestment: {
            amount: capital.estimatedInvestmentAmount ?? '',
            currency: text(capital.currency),
        },
        personalContribution: {
            amount: capital.personalContribution ?? '',
            currency: text(capital.currency),
        },
        loan: {
            amount: capital.loanAmount ?? '',
            currency: text(capital.currency),
        },
        institutionName: text(capital.institutionProvidingLoan),
        bankName: text(capital.bankName),
        bankAddress: text(capital.bankAddress),
        shareHolders: (capital.shareholders ?? []).map((holder: any) => ({
            salutation: text(holder?.salutation),
            firstName: text(holder?.firstName),
            middleName: text(holder?.middleName),
            lastName: text(holder?.lastName),
            nationality: text(holder?.nationality),
            numberOfShares: text(holder?.numberOfShares),
        })),

        // Land and construction
        landSizeHectares: construction.landSizeInHectares ?? '',
        existingBuildings: construction.existingBuildingsUsed ? 'yes' : 'no',
        constructionCompletionTime: text(construction.constructionCompletionTime),
        projectLifespan: text(
            construction.projectLifespanYearsString ?? construction.projectLifespanYears
        ),
        environmentalManagementPlanDescription: text(construction.environmentalManagementPlans),

        // Workforce
        localPermanentEmployees: workforce.localPermanentEmployees ?? '',
        localTemporaryEmployees: workforce.localTemporaryEmployees ?? '',
        foreignPermanentEmployees: workforce.foreignPermanentEmployees ?? '',
        foreignTemporaryEmployees: workforce.foreignTemporaryEmployees ?? '',
    };
}

function toPayload(values: any, investment: any): InvestmentUpdatePayload {
    const business = investment?.businessDetails ?? {};

    return {
        businessDetails: {
            projectName: values.projectName,
            // Identifiers and licence history the wizard never exposes: sent back
            // unchanged so InvestmentApplication.fromDto does not rebuild the
            // record without them.
            projectNumber: optionalText(business.projectNumber),
            dateIssued: optionalText(business.dateIssued),
            previousLicenseNumber: optionalText(business.previousLicenseNumber),
            previousLicenseDateIssued: optionalText(business.previousLicenseDateIssued),
            currentLicenseName: optionalText(business.currentLicenseName),
            projectType: optionalText(business.projectType),

            businessName: values.companyName,
            zone: values.zone,
            region: values.region,
            district: values.district,
            street: values.street,
            postCode: values.postCode,
            poBox: values.poBox || values.projectPoBox,
            projectActivities: toActivities(values.projectActivities),
            numberOfRooms: optionalNumber(values.numberOfRooms),
        },
        capitalInvestment: {
            companyOperatingProject: values.companyName,
            estimatedInvestmentAmount: toAmount(values.estimatedInvestment, values.estimatedInvestment?.currency),
            currency: optionalText(values.estimatedInvestment?.currency),
            personalContribution: toAmount(values.personalContribution, values.personalContribution?.currency),
            loanAmount: toAmount(values.loan, values.loan?.currency),
            institutionProvidingLoan: values.institutionName,
            bankName: values.bankName,
            bankAddress: values.bankAddress,
            shareholders: (values.shareHolders ?? []).map((holder: any) => ({
                salutation: text(holder?.salutation),
                firstName: text(holder?.firstName),
                middleName: text(holder?.middleName),
                lastName: text(holder?.lastName),
                nationality: text(holder?.nationality),
                numberOfShares: text(holder?.numberOfShares),
            })),
        },
        construction: {
            landSizeInHectares: optionalText(values.landSizeHectares),
            existingBuildingsUsed: values.existingBuildings === 'yes' ? 'true' : 'false',
            constructionCompletionTime: values.constructionCompletionTime,
            projectLifespanYears: optionalText(values.projectLifespan),
            environmentalManagementPlans: values.environmentalManagementPlanDescription,
        },
        workforce: {
            localPermanentEmployees: text(values.localPermanentEmployees),
            localTemporaryEmployees: text(values.localTemporaryEmployees),
            foreignPermanentEmployees: text(values.foreignPermanentEmployees),
            foreignTemporaryEmployees: text(values.foreignTemporaryEmployees),
        },
    };
}

function InvestmentEditForm({ applicationId, investment }: { applicationId: string; investment: any }) {
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(1);
    const detailsPath = `/applications/investments/applications-details/${applicationId}`;

    const { mutate, isPending } = useUpdateInvestment(applicationId, () => navigate(detailsPath));

    const methods = useForm<any>({ defaultValues: toFormValues(investment) });

    const steps = [
        {
            step: 1,
            title: 'Project Details',
            isActive: currentStep === 1,
            isCompleted: currentStep > 1,
            image: <LicenceDetails isCompleted={currentStep > 1} isActive={currentStep === 1} />,
        },
        {
            step: 2,
            title: 'Capital Investment',
            isActive: currentStep === 2,
            isCompleted: currentStep > 2,
            image: <CapitalInvestment isCompleted={currentStep > 2} isActive={currentStep === 2} />,
        },
        {
            step: 3,
            title: 'Construction ',
            isActive: currentStep === 3,
            isCompleted: currentStep > 3,
            image: <Construction isCompleted={currentStep > 3} isActive={currentStep === 3} />,
        },
        {
            step: 4,
            title: 'Work Forces',
            isActive: currentStep === 4,
            isCompleted: currentStep >= 4,
            image: <Workforce isCompleted={currentStep >= 4} isActive={currentStep === 4} />,
        },
    ];

    const handleNext = () => {
        if (currentStep < steps.length) setCurrentStep((step) => step + 1);
    };

    const handleBack = () => {
        if (currentStep > 1) setCurrentStep((step) => step - 1);
    };

    const onSubmit: SubmitHandler<any> = (values) => {
        mutate(toPayload(values, investment));
    };

    /** Send the applicant to the step that actually holds the problem, rather
     *  than leaving them on a step with no visible error. */
    const onInvalid = (errors: Record<string, unknown>) => {
        const failing = Object.keys(errors);
        const step = Object.keys(STEP_FIELDS)
            .map(Number)
            .sort((a, b) => a - b)
            .find((candidate) => STEP_FIELDS[candidate].some((field) => failing.includes(field)));
        if (step) setCurrentStep(step);
        toast.error('Please fill all the required fields');
    };

    const renderStepContent = () => {
        switch (currentStep) {
            case 1:
                return <ProjectForm />;
            case 2:
                return <CapitalInvestmentForm />;
            case 3:
                return <LandConstractionsForm />;
            case 4:
                return <WorkForcesForm />;
            default:
                return null;
        }
    };

    return (
        <div className="w-full">
            <div className="flex justify-between items-center border-b border-gray-200 px-4 py-2">
                <Link to={detailsPath}>
                    <Button
                        type="button"
                        intent="text"
                        leftIcon={<SquareArrowLeft className="size-5" />}
                        className="text-base text-secondary font-light"
                    >
                        Edit Application
                    </Button>
                </Link>
                <div className="flex justify-between items-center gap-3 my-1">
                    <Button
                        type="button"
                        intent="default"
                        size="md"
                        onClick={handleBack}>
                        Back
                    </Button>
                    <Button
                        type="button"
                        intent="primary"
                        className={`${currentStep === steps.length && 'hidden'}`}
                        size="md"
                        onClick={handleNext}>
                        Continue
                    </Button>
                </div>
            </div>
            <div className="flex space-x-5 gap-5 px-4">
                <div>
                    <VerticalStepper steps={steps} />
                </div>
                <div className="mt-2 ml-10 w-full max-w-3xl">
                    <FormProvider {...methods}>
                        <form className="w-full">
                            {renderStepContent()}
                            <div className="flex justify-end w-full gap-2 mt-3">
                                <Link to={detailsPath}>
                                    <Button type="button" intent="default" size="md">
                                        Cancel
                                    </Button>
                                </Link>
                                <Button
                                    type="button"
                                    intent="primary"
                                    loading={isPending}
                                    disabled={isPending}
                                    size="md"
                                    onClick={methods.handleSubmit(onSubmit, onInvalid)}>
                                    Save changes
                                </Button>
                            </div>
                        </form>
                    </FormProvider>
                </div>
            </div>
        </div>
    );
}

export default function InvestmentApplicationEdit() {
    const { applicationId } = useParams();
    const { investiment, isLoading } = useGetInvestmentDetails(applicationId!);

    const investment = (investiment as any)?.results ?? investiment;

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-screen">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
            </div>
        );
    }

    if (!investment) {
        return (
            <div className="px-4 py-6">
                <p className="text-sm text-gray-700">This application could not be loaded.</p>
            </div>
        );
    }

    if (!isEditableStatus(investment.applicationStatus)) {
        return (
            <div className="px-4 py-6">
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-4">
                    <h1 className="text-base font-bold text-[#2C2C2C]">
                        This application can no longer be edited
                    </h1>
                    <p className="mt-1 text-sm text-amber-900">
                        It is {statusLabel(investment.applicationStatus) ?? 'in a closed state'}.
                    </p>
                    <Link to={`/applications/investments/applications-details/${applicationId}`}>
                        <Button type="button" intent="primary" size="md" className="mt-3 ml-0">
                            Back to application
                        </Button>
                    </Link>
                </div>
            </div>
        );
    }

    return <InvestmentEditForm applicationId={applicationId!} investment={investment} />;
}
